import React, { useMemo } from "react";
import { Box, Typography, RadioGroup, FormControlLabel, Radio, FormControlLabel as CheckboxLabel, Checkbox } from "@mui/material";

const phqMapping = {
  0: "Not at All",
  1: "Several days",
  2: "More than half the days",
  3: "Nearly every day",
};

const phqReverseMapping = Object.fromEntries(Object.entries(phqMapping).map(([k, v]) => [v, k]));

const gadMapping = {
  0: "Not At All",
  1: "Several days",
  2: "More than half the days",
  3: "Nearly every day",
};

const gadReverseMapping = Object.fromEntries(Object.entries(gadMapping).map(([k, v]) => [v, k]));

const phqQuestions = [
  "Little interest or pleasure in doing things?",
  "Feeling down, depressed, or hopeless?",
  "Trouble falling or staying asleep, or sleeping too much?",
  "Feeling tired or having little energy?",
  "Poor appetite or overeating?",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down?",
  "Trouble concentrating on things, such as reading the newspaper or watching television?",
  "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual?",
  "Thoughts that you would be better off dead or of hurting yourself in some way?",
];

const gadQuestions = [
  "Feeling nervous, anxious or on edge?",
  "Not being able to stop or control worrying?",
  "Worrying too much about different things?",
  "Trouble relaxing?",
  "Being so restless that it is hard to sit still?",
  "Becoming easily annoyed or irritable?",
  "Feeling afraid as if something awful might happen?",
];

const PhqGadForm = ({ data, onChange, templateId, isEditing = true }) => {
  const is773 = templateId === "100001773";

  const phqValues = useMemo(() => phqQuestions.map((_, i) => phqReverseMapping[data[`PHQ9_${i+1}`] || "Not at All"]), [data]);
  const gadValues = useMemo(() => gadQuestions.map((_, i) => gadReverseMapping[data[`GAD7_${i+1}`] || "Not At All"]), [data]);
  const phq10 = data.PHQ9_10 || "Not Difficult at All";
  const phqTotal = data.PHQ9_TS || phqValues.reduce((a, b) => a + Number(b), 0);
  const gadTotal = data.GAD7_Score || gadValues.reduce((a, b) => a + Number(b), 0);

  const handlePhqChange = (i, value) => {
    if (isEditing) {
      onChange({ ...data, [`PHQ9_${i+1}`]: phqMapping[value] });
    }
  };

  const handleGadChange = (i, value) => {
    if (isEditing) {
      onChange({ ...data, [`GAD7_${i+1}`]: gadMapping[value] });
    }
  };

  const handlePhq10Change = (value) => {
    if (isEditing) {
      onChange({ ...data, PHQ9_10: value });
    }
  };

  const handleGad10Change = (key, checked) => {
    if (isEditing) {
      onChange({ ...data, [key]: checked });
    }
  };

  const handleMiniPhqChange = (key, value) => {
    if (isEditing) {
      onChange({ ...data, [key]: phqMapping[value] });
    }
  };

  const handleScoreReviewedChange = (checked) => {
    if (isEditing) {
      onChange({ ...data, PHQ9scorereviewed: checked });
    }
  };

  return (
    <Box sx={{ mb: 4, p: 3, border: "1px solid #ddd", borderRadius: 2, backgroundColor: "#f9f9f9" }}>
      <Typography variant="h5" gutterBottom>PHQ-9 & GAD-7</Typography>

      {is773 && (
        <>
          <Typography variant="h6">Mini PHQ</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography>1. Little interest or pleasure in doing things?</Typography>
            <RadioGroup row value={phqReverseMapping[data.PHQ9_1_1 || "Not at All"]} onChange={(e) => handleMiniPhqChange("PHQ9_1_1", Number(e.target.value))}>
              <FormControlLabel value={0} control={<Radio disabled={!isEditing} />} label="Not at All" />
              <FormControlLabel value={1} control={<Radio disabled={!isEditing} />} label="Several days" />
              <FormControlLabel value={2} control={<Radio disabled={!isEditing} />} label="More than half the days" />
              <FormControlLabel value={3} control={<Radio disabled={!isEditing} />} label="Nearly every day" />
            </RadioGroup>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography>2. Feeling down, depressed, or hopeless?</Typography>
            <RadioGroup row value={phqReverseMapping[data.PHQ9_2_1 || "Not at All"]} onChange={(e) => handleMiniPhqChange("PHQ9_2_1", Number(e.target.value))}>
              <FormControlLabel value={0} control={<Radio disabled={!isEditing} />} label="Not at All" />
              <FormControlLabel value={1} control={<Radio disabled={!isEditing} />} label="Several days" />
              <FormControlLabel value={2} control={<Radio disabled={!isEditing} />} label="More than half the days" />
              <FormControlLabel value={3} control={<Radio disabled={!isEditing} />} label="Nearly every day" />
            </RadioGroup>
          </Box>
          <CheckboxLabel
            control={<Checkbox checked={data.PHQ9scorereviewed} onChange={(e) => handleScoreReviewedChange(e.target.checked)} disabled={!isEditing} />}
            label="PHQ-9 score reviewed"
          />
        </>
      )}

      <Typography variant="h6">PHQ-9 — Total Score: <strong>{phqTotal}</strong></Typography>
      {phqQuestions.map((q, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Typography>{i + 1}. {q}</Typography>
          <RadioGroup row value={Number(phqValues[i])} onChange={(e) => handlePhqChange(i, Number(e.target.value))}>
            <FormControlLabel value={0} control={<Radio disabled={!isEditing} />} label="Not at All" />
            <FormControlLabel value={1} control={<Radio disabled={!isEditing} />} label="Several days" />
            <FormControlLabel value={2} control={<Radio disabled={!isEditing} />} label="More than half the days" />
            <FormControlLabel value={3} control={<Radio disabled={!isEditing} />} label="Nearly every day" />
          </RadioGroup>
        </Box>
      ))}
      <Box sx={{ mb: 2 }}>
        <Typography>10. If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?</Typography>
        <RadioGroup row value={phq10} onChange={(e) => handlePhq10Change(e.target.value)}>
          <FormControlLabel value="Not Difficult at All" control={<Radio disabled={!isEditing} />} label="Not Difficult at All" />
          <FormControlLabel value="Somewhat Difficult" control={<Radio disabled={!isEditing} />} label="Somewhat Difficult" />
          <FormControlLabel value="Very Difficult" control={<Radio disabled={!isEditing} />} label="Very Difficult" />
          <FormControlLabel value="Extremely Difficult" control={<Radio disabled={!isEditing} />} label="Extremely Difficult" />
        </RadioGroup>
      </Box>

      <Typography variant="h6">GAD-7 — Total Score: <strong>{gadTotal}</strong></Typography>
      {gadQuestions.map((q, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Typography>{i + 1}. {q}</Typography>
          <RadioGroup row value={Number(gadValues[i])} onChange={(e) => handleGadChange(i, Number(e.target.value))}>
            <FormControlLabel value={0} control={<Radio disabled={!isEditing} />} label="Not At All" />
            <FormControlLabel value={1} control={<Radio disabled={!isEditing} />} label="Several days" />
            <FormControlLabel value={2} control={<Radio disabled={!isEditing} />} label="More than half the days" />
            <FormControlLabel value={3} control={<Radio disabled={!isEditing} />} label="Nearly every day" />
          </RadioGroup>
        </Box>
      ))}
      <Box>
        <Typography>10. If you are experiencing any of the problems on this form, how difficult have these problems made it for you to do your work, take care of things at home or get along with other people?</Typography>
        <CheckboxLabel control={<Checkbox checked={data.GAD7_10a} onChange={(e) => handleGad10Change("GAD7_10a", e.target.checked)} disabled={!isEditing} />} label="Not difficult at all" />
        <CheckboxLabel control={<Checkbox checked={data.GAD7_10b} onChange={(e) => handleGad10Change("GAD7_10b", e.target.checked)} disabled={!isEditing} />} label="Somewhat difficult" />
        <CheckboxLabel control={<Checkbox checked={data.GAD7_10c} onChange={(e) => handleGad10Change("GAD7_10c", e.target.checked)} disabled={!isEditing} />} label="Very difficult" />
        <CheckboxLabel control={<Checkbox checked={data.GAD7_10d} onChange={(e) => handleGad10Change("GAD7_10d", e.target.checked)} disabled={!isEditing} />} label="Extremely difficult" />
      </Box>
    </Box>
  );
};

export default PhqGadForm;